import type { CompetitorOverview } from "@/lib/api/types";
import { ownPosition } from "@/lib/competitors";
import { formatCount } from "@/lib/format";

type Gap = NonNullable<CompetitorOverview["gap"]>;

type Props = {
  gap: Gap;
  onBriefing: (keyword: string) => void;
};

const TH = "px-3 py-2 font-medium";
const TD = "px-3 py-2.5 text-[13px]";

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex min-w-[170px] flex-1 flex-col gap-0.5 rounded-md border border-neutral-bg px-4 py-3">
      <span className="text-xs text-muted">{label}</span>
      <span className="font-mono text-xl font-semibold">{value}</span>
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </div>
  );
}

/**
 * Ergebnis der Content-Gap: Keywords, für die Wettbewerber in den Top 10 stehen und die eigene Domain nicht
 * (fehlt) oder erst ab Platz 11 (schwach). Gebündelt nach der Seite des besten Wettbewerbers: Eine Seite mit
 * vielen Lücken ist ein Thema, das ein eigenes Briefing wert ist. Das Potenzial schätzt die Besuche im Monat
 * aus Suchvolumen und Klickrate der besten Wettbewerber-Position.
 */
export function GapResult({ gap, onBriefing }: Props) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <Kpi label="Keywords der Wettbewerber" value={formatCount(gap.competitor_keywords)} hint={`Top 10, ab ${formatCount(gap.min_volume)} Suchanfragen`} />
        <Kpi label="Bereits abgedeckt" value={formatCount(gap.covered)} hint="eigene Domain in den Top 10" />
        <Kpi label="Schwach (Platz 11 und tiefer)" value={formatCount(gap.weak)} />
        <Kpi label="Fehlend (kein Ranking)" value={formatCount(gap.missing)} />
        <Kpi label="Potenzial" value={`ca. ${formatCount(gap.potential)}`} hint="Besuche im Monat, grobe Schätzung" />
      </div>

      {gap.groups.length === 0 && (
        <p className="text-sm text-muted">Keine Lücken gefunden: Für alle Keywords der gewählten Wettbewerber steht die eigene Domain in den Top 10.</p>
      )}

      {gap.groups.map((group) => (
        <article
          key={`${group.domain}|${group.url ?? ""}`}
          aria-label={`Thema ${group.lead_keyword}`}
          className="flex flex-col gap-2 rounded-lg border border-line px-4 py-3"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <h3 className="text-sm font-semibold">{group.lead_keyword}</h3>
              <span className="break-all text-xs text-muted">
                {group.url ?? group.domain} · {formatCount(group.keyword_count)} {group.keyword_count === 1 ? "Keyword" : "Keywords"} (
                {formatCount(group.missing)} fehlen, {formatCount(group.weak)} schwach) · Suchvolumen {formatCount(group.volume)} · Potenzial ca.{" "}
                {formatCount(group.potential)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onBriefing(group.lead_keyword)}
              aria-label={`Briefing zu ${group.lead_keyword} starten`}
              className="h-11 rounded-md border border-field bg-white px-3 text-[13px]"
            >
              Briefing starten
            </button>
          </div>
          <div className="overflow-x-auto">
            <table aria-label={`Keywords zu ${group.lead_keyword}`} className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className={TH}>Keyword</th>
                  <th className={`${TH} text-right`}>Suchanfragen</th>
                  <th className={`${TH} text-right`}>Wettbewerber</th>
                  <th className={TH}>Eigene Domain</th>
                </tr>
              </thead>
              <tbody>
                {group.keywords.map((keyword) => (
                  <tr key={keyword.keyword} className="border-t border-neutral-bg">
                    <td className={TD}>{keyword.keyword}</td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(keyword.volume)}</td>
                    <td className={`${TD} text-right font-mono`}>
                      Platz {formatCount(keyword.position)}
                      {keyword.competitors > 1 ? ` (+${formatCount(keyword.competitors - 1)})` : ""}
                    </td>
                    <td className={TD}>{ownPosition(keyword.state, keyword.own_position)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {group.keyword_count > group.keywords.length && (
            <span className="text-xs text-muted">
              {formatCount(group.keyword_count - group.keywords.length)} weitere Keywords dieser Seite nicht aufgeführt.
            </span>
          )}
        </article>
      ))}
      {gap.group_count > gap.groups.length && (
        <p className="text-xs text-muted">
          Es werden die {formatCount(gap.groups.length)} Themen mit dem größten Potenzial gezeigt, {formatCount(gap.group_count - gap.groups.length)} weitere
          sind nicht aufgeführt.
        </p>
      )}
    </div>
  );
}
